import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Steps, message, Spin, Typography, Space, Tag, Row, Col, Descriptions, Progress, Empty, Select, Modal, List, Popconfirm } from 'antd';
import { ArrowLeftOutlined, CodeOutlined, UserAddOutlined, DeleteOutlined, ShareAltOutlined, RobotOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

const { Title, Paragraph, Text } = Typography;

const PHASE_LABELS: Record<string, string> = { design: '设计模式', development: '开发模式' };
const phaseColors: Record<string, string> = { design: '#1677ff', development: '#0f766e' };
const phaseDescriptions: Record<string, string> = {
  design: '需求梳理、产品方案、技术架构与设计确认',
  development: '代码实现、质量验证和交付准备',
};
const PHASE_ORDER = ['design', 'development'];

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useAuthStore.getState().user;
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // 共享
  const [shares, setShares] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [sharing, setSharing] = useState(false);

  useEffect(() => { if (id) { loadProject(id); loadShares(id); loadUsers(); } }, [id]);

  const loadProject = async (projectId: string) => {
    setLoading(true);
    try { const r = await api.get(`/projects/${projectId}`); setProject(r.data); }
    catch { message.error('加载项目失败'); navigate('/projects'); }
    finally { setLoading(false); }
  };

  const loadShares = async (projectId: string) => {
    try { const r = await api.get(`/projects/${projectId}/shares`); setShares(r.data || []); } catch {}
  };

  const loadUsers = async () => {
    try { const r = await api.get('/users'); setUsers((r.data?.items||[]).filter((u:any) => u.id !== currentUser?.id)); } catch {}
  };

  const handleShare = async () => {
    if (!id || !selectedUsers.length) return;
    setSharing(true);
    try {
      await api.post(`/projects/${id}/shares`, { userIds: selectedUsers });
      message.success('共享成功');
      setShareModalOpen(false);
      setSelectedUsers([]);
      loadShares(id);
    } catch (e: any) { message.error(e.response?.data?.message || '共享失败'); }
    setSharing(false);
  };

  const handleRevoke = async (userId: string) => {
    if (!id) return;
    try {
      await api.delete(`/projects/${id}/shares/${userId}`);
      message.success('已取消共享');
      loadShares(id);
    } catch (e: any) { message.error(e.response?.data?.message || '取消失败'); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}><Spin size="large" /></div>;
  if (!project) return <Empty description="未找到项目" />;

  const currentPhaseIndex = PHASE_ORDER.indexOf(project.status);
  const progress = (() => {
    const idx = PHASE_ORDER.indexOf(project.status);
    if (idx <= 0) return 0; // design阶段无产出=0%
    return Math.round((idx / PHASE_ORDER.length) * 100);
  })();
  const isOwner = project.ownerId === currentUser?.id;
  const isBusinessAgent = project.settings?.projectKind === 'business_agent';
  const workspaceButtonText = isBusinessAgent ? '打开智能体工坊' : '打开AI全流程';

  return (
    <div style={{ padding: '4px 0' }}>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <Card className="lg-hero-card" style={{ color: '#fff' }}>
          <Space style={{ marginBottom: 16 }}>
            <Button type="text" icon={<ArrowLeftOutlined />} style={{ color: '#fff' }} onClick={() => navigate('/projects')}>返回</Button>
          </Space>
          <Row gutter={[24, 16]} align="middle">
            <Col xs={24} lg={16}>
              <Title level={2} style={{ color: '#fff', margin: 0 }}>
                {project.name}
                {isBusinessAgent && <Tag color="cyan" icon={<RobotOutlined />} style={{ marginLeft: 12 }}>智能体开发</Tag>}
                {!isOwner && project.owner && <Tag color="orange" style={{marginLeft:12,fontSize:12}}>来自 {project.owner.username || project.owner.name}</Tag>}
              </Title>
              <Paragraph style={{ color: 'rgba(255,255,255,.8)', marginTop: 8, marginBottom: 16, fontSize: 15 }}>
                {project.description || `工作区: ${project.workspacePath || '未设置'}`}
              </Paragraph>
              <Space wrap>
                <Tag color="rgba(255,255,255,.2)" style={{ color: '#fff', borderRadius: 8, border: '1px solid rgba(255,255,255,.3)' }}>
                  {PHASE_LABELS[project.status] || project.status}
                </Tag>
                <Text style={{ color: 'rgba(255,255,255,.7)', fontSize: 13 }}>
                  {project.createdAt ? new Date(project.createdAt).toLocaleDateString('zh-CN') : '-'}
                </Text>
              </Space>
            </Col>
            <Col xs={24} lg={8}>
              <div style={{ textAlign: 'center' }}>
                <Progress type="circle" percent={progress} strokeColor={{ '0%': '#fff', '100%': 'rgba(255,255,255,.6)' }} trailColor="rgba(255,255,255,.15)" format={(p) => <span style={{ color: '#fff', fontSize: 20 }}>{p}%</span>} size={100} />
                <div style={{ color: 'rgba(255,255,255,.7)', marginTop: 8, fontSize: 13 }}>整体进度</div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* ═══════ 项目共享 ═══════ */}
        {isOwner && (
          <Card className="lg-glass-card" title={<Space><ShareAltOutlined />项目共享</Space>}
            extra={<Button type="primary" size="small" icon={<UserAddOutlined />} onClick={() => setShareModalOpen(true)}>添加共享</Button>}
          >
            {shares.length === 0 ? (
              <Text type="secondary">尚未共享给其他用户，点击"添加共享"选择用户</Text>
            ) : (
              <List size="small" dataSource={shares}
                renderItem={(s: any) => (
                  <List.Item actions={[
                    <Popconfirm title={`确认取消对 ${s.sharedWithUser?.name || s.sharedWithUser?.username} 的共享？`} onConfirm={() => handleRevoke(s.sharedWithUserId)}>
                      <Button size="small" danger type="text" icon={<DeleteOutlined />}>取消共享</Button>
                    </Popconfirm>
                  ]}>
                    <List.Item.Meta
                      avatar={<Tag color="blue">{s.sharedWithUser?.username}</Tag>}
                      title={s.sharedWithUser?.name || s.sharedWithUser?.username}
                      description={`共享于 ${new Date(s.createdAt).toLocaleString('zh-CN')}`}
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        )}

        <Card className="lg-glass-card" bodyStyle={{ padding: 24 }}>
          <Space style={{ width:'100%', justifyContent:'space-between', marginBottom:16 }}>
            <Text strong style={{ fontSize: 16 }}>项目阶段</Text>
            <Button type="primary" icon={isBusinessAgent ? <RobotOutlined /> : <CodeOutlined />} onClick={() => navigate(`/dev-workspace?projectId=${project.id}`)}>{workspaceButtonText}</Button>
          </Space>
          <Steps current={currentPhaseIndex} size="small"
            items={PHASE_ORDER.map((phase, idx) => ({
              title: PHASE_LABELS[phase],
              status: idx < currentPhaseIndex ? 'finish' : idx === currentPhaseIndex ? 'process' : 'wait',
            }))} />
        </Card>

        <Row gutter={[20, 20]}>
          <Col xs={24} lg={14}>
            <Card className="lg-glass-card" title="项目信息" bodyStyle={{ padding: 24 }}>
              <Descriptions bordered size="small" column={1} style={{ marginBottom: 20 }}>
                <Descriptions.Item label="名称">{project.name}</Descriptions.Item>
                <Descriptions.Item label="状态"><Tag color={phaseColors[project.status]}>{PHASE_LABELS[project.status]}</Tag></Descriptions.Item>
                <Descriptions.Item label="工作区">{project.workspacePath || '-'}</Descriptions.Item>
                <Descriptions.Item label="描述">{project.description || '-'}</Descriptions.Item>
                <Descriptions.Item label="项目类型">{isBusinessAgent ? '高速公路行业智能体' : '普通应用'}</Descriptions.Item>
                <Descriptions.Item label="创建时间">{project.createdAt ? new Date(project.createdAt).toLocaleString('zh-CN') : '-'}</Descriptions.Item>
              </Descriptions>
              <Button type="primary" icon={isBusinessAgent ? <RobotOutlined /> : <CodeOutlined />} onClick={() => navigate(`/dev-workspace?projectId=${project.id}`)}>{workspaceButtonText}</Button>
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card className="lg-glass-card" title="阶段说明">
              {PHASE_ORDER.map((phase, idx) => (
                <div key={phase} style={{ marginBottom: 12 }}>
                  <Space>
                    <Tag color={idx < currentPhaseIndex ? 'green' : idx === currentPhaseIndex ? phaseColors[phase] : '#d9d9d9'}>
                      {idx < currentPhaseIndex ? '✅' : idx === currentPhaseIndex ? '🔄' : '○'}
                    </Tag>
                    <Text strong={idx === currentPhaseIndex}>{PHASE_LABELS[phase]}</Text>
                  </Space>
                  <div><Text type="secondary" style={{ fontSize: 12 }}>{phaseDescriptions[phase]}</Text></div>
                </div>
              ))}
            </Card>
          </Col>
        </Row>
      </Space>

      {/* ── 共享选择弹窗 ── */}
      <Modal title="选择共享用户" open={shareModalOpen} onCancel={() => { setShareModalOpen(false); setSelectedUsers([]); }}
        onOk={handleShare} confirmLoading={sharing} okText="确认共享"
      >
        <Select mode="multiple" style={{ width: '100%' }} placeholder="选择要共享的用户"
          value={selectedUsers} onChange={setSelectedUsers}
          options={users.map((u: any) => ({ value: u.id, label: `${u.username} (${u.name})` }))}
          filterOption={(input, option) => (option?.label as string || '').toLowerCase().includes(input.toLowerCase())}
        />
      </Modal>
    </div>
  );
}
